import React, { useState, useEffect, useRef, useMemo } from "react";
import { Heart, Star } from "lucide-react";
import { toast } from "react-toastify";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../../redux/api/website/AddtoWishlistApi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import prodimg from "../../../../assets/windTree/images/login/logowbg.webp";
import { addToWishlistLocal, removeFromWishlistLocal } from "../../../redux/reduxSlice/website/WishlistSlice";
import { hasBestSellerTag } from "./utils";
import rollerImg from "../../../../assets/windTree/images/category/roller.jpg"
import zebraImg from "../../../../assets/windTree/images/category/zebra.jpg"

const ProductCard = ({ product, index = 0 }) => {
  const mappedProduct = useMemo(() => {
    const mapped = {
      ROWID: product.ROWID || product.ROW_ID || product.id || "",
      name: product.shadeNo || product.shadeName || product.name || "Product",
      rating: 4.5,

      sellingPrice:
        product.items &&
          Array.isArray(product.items) &&
          product.items.length > 0 &&
          product.items[0]?.sellingPrice
          ? parseFloat(product.items[0].sellingPrice)
          : parseFloat(product.sellingPrice) || 0,

      oldPrice:
        product.items &&
          Array.isArray(product.items) &&
          product.items.length > 0 &&
          product.items[0]?.costPrice
          ? parseFloat(product.items[0].costPrice)
          : parseFloat(product.costPrice) || 0,

      images: (() => {
        let media = product.shadeMedia;

        if (typeof media === "string") {
          try {
            media = JSON.parse(media);
          } catch {
            media = [];
          }
        }

        if (Array.isArray(media)) {
          return media.map((img) => ({
            imageUrl: img,
            alt: product.shadeNo || "Product image",
          }));
        }

        if (
          product.items &&
          Array.isArray(product.items) &&
          product.items.length > 0 &&
          product.items[0]?.itemImages
        ) {
          try {
            const parsedImages = JSON.parse(product.items[0].itemImages);
            return parsedImages.map((img) => ({
              imageUrl: img.filePath,
              alt: img.name || product.shadeNo,
            }));
          } catch {
            return [];
          }
        }

        return [];
      })(),

      description:
        product.description ||
        (product.items && product.items[0]?.sellingDescription) || "",

      category:
        (product.items && product.items[0]?.categoryName) ||
        product.shadeCategory ||
        "",

      type: (product.items && product.items[0]?.type) || product.type || "",
      sku: (product.items && product.items[0]?.sku) || product.sku || "",

      availableStock:
        product.items && product.items[0]?.availableStock
          ? parseInt(product.items[0].availableStock)
          : parseInt(product.availableStock) || 0,

      sale: false,
      new: false,
      popular: false,
    };
    return mapped;
  }, [product]);

  const getTagsData = useSelector((state) => state?.tags?.tags);
  const wishlistItems = useSelector(
    (state) => state?.Wishlist?.wishlist || []
  );
  const wishlistItemIds = useMemo(
    () => wishlistItems.map((item) => item.wishLists?.wishListItem),
    [wishlistItems]
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const intervalRef = useRef(null);
  const isProductLiked = wishlistItemIds.includes(mappedProduct.ROWID);

  const stars = useMemo(() => {
    const starsArr = [];
    for (let i = 0; i < 5; i++) {
      const fillPercent =
        Math.min(Math.max(mappedProduct.rating - i, 0), 1) * 100;
      starsArr.push(
        <div key={i} className="relative w-4 h-4">
          <Star size={16} className="text-gray-300" />
          <div
            className="absolute top-0 left-0 overflow-hidden"
            style={{ width: `${fillPercent}%` }}
          >
            <Star size={16} className="text-yellow-400 fill-yellow-400" />
          </div>
          </div>
      )
    }
    return <div className="flex gap-0.5">{starsArr}</div>;
  }, [mappedProduct.rating]);

  // Handle image cycling on hover — only after image has loaded
  useEffect(() => {
    if (mappedProduct.images.length > 1 && isHovered && imgLoaded) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex(
          (prev) => (prev + 1) % mappedProduct.images.length
        );
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setCurrentImageIndex(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, mappedProduct.images.length, imgLoaded]);

  // Reset imgLoaded when image source changes
  useEffect(() => {
    setImgLoaded(false);
  }, [currentImageIndex]);

  const goToImage = (idx, e) => {
    e.stopPropagation();
    setCurrentImageIndex(idx);
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggleLike = async (e, productId) => {
    e.stopPropagation();

    const accountId = localStorage.getItem("UserRowId");
    if (!accountId) {
      toast.error("Please login to add items to wishlist");
      navigate("/login");
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    const isCurrentlyLiked = wishlistItemIds.includes(productId);
    try {
      if (!isCurrentlyLiked) {
        const resp = await addToWishList(productId, accountId);
        toast.success("Product added to wishlist");
        dispatch(
          addToWishlistLocal({
            ROWID: resp.rowid,
            wishListItem: productId,
            accountId: accountId,
          })
        );
      } else {
        const wishlistItem = wishlistItems.find(
          (item) => item?.wishLists?.wishListItem === productId
        );
        const wishlistRowId = wishlistItem?.wishLists?.ROWID;
        if (wishlistRowId) {
          await removeFromWishList(wishlistRowId);
          dispatch(removeFromWishlistLocal(wishlistRowId));
          toast.success("Product removed from wishlist");
        } else {
          throw new Error("Could not find wishlist item ID");
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Wishlist operation error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addToWishList = async (productId, accountId) => {
    const wishlistData = {
      wishListItem: productId,
      accountId: accountId,
    };

    try {
      const response = await dispatch(addToWishlist(wishlistData)).unwrap();
      if (response?.success && !response.error) {
        return response;
      } else {
        throw new Error(response?.message || "Failed to add");
      }
    } catch (error) {
      throw error;
    }
  };

  const removeFromWishList = async (wishlistRowId) => {
    const deletePayload = [wishlistRowId];
    try {
      const response = await dispatch(
        removeFromWishlist(deletePayload)
      ).unwrap();
      if (response?.success) {
        return true;
      } else {
        throw new Error(response?.message || "Failed to remove");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleCardClick = () => {
    navigate(`/shop/single-product/${mappedProduct.ROWID}`)
  };

  const showBestSeller = hasBestSellerTag(product.tagsId, getTagsData);
  const currentImage = mappedProduct.images[currentImageIndex];

  const productCode = mappedProduct.name.trim().toLowerCase();

const fallbackImage =
    productCode.startsWith("rp")
    ? rollerImg
      : productCode.startsWith("zp")
      ? zebraImg
      : prodimg;

const imageSrc = currentImage?.imageUrl || fallbackImage;
const imageAlt = currentImage?.alt || mappedProduct.name;

  // Priority hints for first 4 cards (above the fold)
  const loadingAttr = index < 4 ? "eager" : "lazy";
  const fetchPriority = index < 4 ? "high" : "auto";

  return (
    <div className="h-[87%]">
      <div
        className="relative z-10 my-0 sm:my-2 w-full max-w-[250px] lg:max-w-none mx-auto hover:scale-105 transform rounded-xl border border-gray-200 shadow-md transition duration-300 cursor-pointer hover:shadow-2xl h-[230px] md:h-[300px] group bg-[#f5fdf9]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Best Seller Tag */}
        {showBestSeller && (
          <div className="absolute top-2 left-2 z-20">
            <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded-md shadow-md animate-pulse">
              BEST SELLER
            </span>
          </div>
        )}

        {/* Wishlist Icon */}
        <button
          className={`absolute top-2 right-2 z-10 bg-[#f2fcf7] rounded-full p-1 opacity-[70%] hover:opacity-[100%] transition ${
            isProductLiked ? "text-red-500" : "text-gray-400 hover:text-red-500"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={(e) => toggleLike(e, mappedProduct.ROWID)}
          disabled={isLoading}
          aria-label={
            isProductLiked ? "Remove from wishlist" : "Add to wishlist"
          }
        >
          <Heart
            size={22}
            fill={isProductLiked ? "currentColor" : "none"}
            stroke={isProductLiked ? "red" : "currentColor"}
            strokeWidth={1.8}
          />
        </button>

        {/* Image Section */}
        <div
          className="relative w-full h-[75%] cursor-pointer overflow-hidden rounded-t-xl bg-gray-100"
          onClick={handleCardClick}
        >
          {!imgLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gray-200" />
          )}

          <img
            src={imageSrc}
            alt={imageAlt}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
              imgLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{ minHeight: "100px" }}
            loading={loadingAttr}
            fetchpriority={fetchPriority}
            onLoad={() => setImgLoaded(true)}
            onError={(e) => {
              e.target.src = fallbackImage;
              setImgLoaded(true);
            }}
          />

          {mappedProduct.images.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 z-10">
              {mappedProduct.images.map((_, idx) => (
                <button
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentImageIndex
                      ? "bg-amber-600 scale-125"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  onClick={(e) => goToImage(idx, e)}
                  aria-label={`View image ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Bottom Content */}
        <div className="h-[25%] px-2 pt-1 relative" onClick={handleCardClick}>
          <div className="absolute top-0 left-0 right-0 -mt-2 z-20">
            <div className="flex items-center justify-start px-2 py-1 pt-3">
              {stars}
              <span className="text-xs text-gray-700 ml-1">
                {mappedProduct.rating?.toFixed(1)}
              </span>
            </div>

          <div className="flex justify-between items-start">
            <h4 className="text-base absolute top-10 sm:text-lg font-bold text-gray-900 truncate text-start w-full font-playfair px-2">
              {mappedProduct.name}
            </h4>
          </div>
      </div>
      </div>
      </div>
      </div>
  );
};

export default React.memo(ProductCard, (prevProps, nextProps) => {
  return (
    prevProps.product?.ROWID === nextProps.product?.ROWID &&
    prevProps.index === nextProps.index
  );
});
